/**
 * GoogleAuthCallback — landing page after the white-labeled Google
 * OAuth consent screen. Google redirects here with `?code=...&state=...`;
 * we validate the state, POST the code + this exact redirect_uri to
 * /api/auth/google/exchange, then bounce the user back to wherever
 * they were when they hit "Sign in with Google".
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { journeyResume } from '../lib/journey';

const GoogleAuthCallback = () => {
  const { exchangeGoogleCode, startGoogleLogin } = useUserAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthErr = params.get('error');
    let expectedState = null;
    try { expectedState = window.sessionStorage.getItem('flyt_oauth_state'); } catch { /* ignore */ }
    let returnPath = '/profile';
    try {
      returnPath = window.sessionStorage.getItem('flyt_oauth_return') || '/profile';
    } catch { /* ignore */ }

    // User denied consent or Google returned an error → bounce home
    // with a friendly toast.
    // iter880 · App Health — finish the web attempt started in startGoogleLogin.
    const j = journeyResume('login_google', 'flyt_journey_google');
    if (oauthErr) {
      if (oauthErr === 'access_denied') j?.fail({ message: 'cancelled', code: 'cancel' });
      else j?.fail(new Error(`Google returned error=${oauthErr}`));
      setError('Google sign-in was cancelled.');
      setTimeout(() => navigate(returnPath, { replace: true }), 1200);
      return;
    }
    if (!code) {
      j?.fail(new Error('Missing authorization code from Google'));
      setError('Missing authorization code from Google.');
      return;
    }
    // CSRF check — only enforced when we recorded a state; older tabs
    // that started before the state was stored are allowed through so
    // we don't lock people out mid-flow.
    if (expectedState && state && state !== expectedState) {
      j?.fail(new Error('OAuth state mismatch'));
      setError('Sign-in state mismatch. Please try again.');
      return;
    }

    // Build the exact same redirect_uri the /startGoogleLogin flow used
    // so Google's token endpoint accepts the swap.
    const redirectUri = window.location.origin + '/auth/google';

    (async () => {
      try {
        await exchangeGoogleCode(code, redirectUri);
        j?.ok();
        try {
          window.sessionStorage.removeItem('flyt_oauth_state');
          window.sessionStorage.removeItem('flyt_oauth_return');
        } catch { /* ignore */ }
        navigate(returnPath, { replace: true });
      } catch (e) {
        // 503 → server side white-label isn't configured; retry through
        // the Emergent-managed flow so the user isn't stuck.
        if (e?.response?.status === 503) {
          j?.fail(e);
          startGoogleLogin(returnPath);
          return;
        }
        j?.fail(e);
        setError(e?.response?.data?.detail || 'Could not complete sign-in.');
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="google-auth-callback">
      <div className="text-center max-w-sm">
        {error ? (
          <>
            <p className="font-italic text-xs uppercase tracking-widest text-rose-300 mb-2">Sign-in error</p>
            <p className="text-sm text-white/80">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/profile', { replace: true })}
              className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white bg-white/10 hover:bg-white/20 ring-1 ring-white/15 transition"
            >
              Back to profile
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin text-white/60 mx-auto mb-3" />
            <p className="text-sm text-white/70">Finishing sign-in with Google…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
